// D02 Unit 12 — EtherChannel Troubleshooting (TROUBLESHOOTING)
import { note } from '../../ui/widgets.js';
import { ecPair, ecLayout } from './topologies.js';
import { ran, bundled, reach } from './helpers.js';
import { createInterface } from '../../engine/ios/switching.js';

function network() {
  const n = ecPair({ bundled: true });
  const [SW1, SW2] = [n.devices.SW1, n.devices.SW2];
  // Fault 1: SW1 was re-templated with passive on both members (SW2 is passive too).
  for (const p of ['GigabitEthernet0/1', 'GigabitEthernet0/2']) SW1.interfaces[p].channelGroup.mode = 'passive';
  // Fault 2: SW2 Gi0/2 was put in the wrong channel-group.
  const po2 = createInterface(SW2, 'Port-channel2');
  Object.assign(po2, { mode: 'trunk', nativeVlan: 99, allowed: [10, 20, 99] });
  SW2.interfaces['GigabitEthernet0/2'].channelGroup = { id: 2, mode: 'passive' };
  return n;
}

export default {
  id: 'u12', num: '12', kind: 'TROUBLESHOOTING', title: 'EtherChannel Troubleshooting', type: 'Troubleshooting Lab', implemented: true,
  objective: 'Find why a Port-channel will not form, using show etherchannel summary on both ends, LACP modes and channel-group membership.',
  network, layout: ecLayout,
  stages: [
    {
      id: 'work', phase: 'TICKET → INVESTIGATE → FIX', title: 'Po1 is down after a template push', devices: ['SW1', 'SW2'],
      ticket: { id: '2012', priority: 2, from: 'NMS', subject: 'Po1 SW1–SW2 down', body: 'A configuration template was pushed to both switches. Po1 is down on both ends; traffic survives on a single un-bundled link. Design: Po1 = Gi0/1 + Gi0/2, LACP, SW1 active, SW2 passive.' },
      evidence: [
        { id: 's1', text: 'SW1: EtherChannel summary', match: ran('SW1', 'show etherchannel summary') },
        { id: 's2', text: 'SW2: EtherChannel summary', match: ran('SW2', 'show etherchannel summary') },
        { id: 'cfg', text: 'Member configuration inspected on both switches', match: (ev) => ['SW1', 'SW2'].includes(ev.device) && /running-config/.test(ev.key) },
      ],
      tasks: [
        { id: 'po', text: 'Po1 bundled with Gi0/1 and Gi0/2 on both switches', check: (rt) => bundled(rt, 'SW1', 'Port-channel1', 2) && bundled(rt, 'SW2', 'Port-channel1', 2), fail: 'Po1 is not fully bundled on both switches.' },
        { id: 'modes', text: 'SW1 members active (design)', check: (rt) => ['GigabitEthernet0/1', 'GigabitEthernet0/2'].every((p) => rt.net.devices.SW1.interfaces[p].channelGroup?.mode === 'active'), fail: 'SW1 members are not in LACP active mode.' },
        { id: 'e2e', text: 'Users reach across', check: (rt) => reach(rt, 'PC1', '10.10.10.21') && reach(rt, 'PC2', '10.10.20.21'), fail: 'Traffic does not cross.' },
      ],
      question: 'Both ends show LACP, yet nothing bundles. What does LACP need at least one side to do?',
      hints: ['passive + passive never negotiates: one side must be <code>active</code>.', 'On SW2, compare the channel-group number on Gi0/1 and Gi0/2. <code>no channel-group</code> then <code>channel-group 1 mode passive</code>.'],
    },
    {
      id: 'verify', phase: 'VERIFY → DOCUMENT', title: 'Verify', devices: ['SW1', 'SW2'],
      evidence: [
        { id: 'a', text: 'SW1: Po1(SU) with two (P) members', match: ran('SW1', 'show etherchannel summary', /Po1\(SU\)\s+LACP\s+Gi0\/1\(P\) Gi0\/2\(P\)/) },
        { id: 'b', text: 'SW2: Po1(SU) with two (P) members', match: ran('SW2', 'show etherchannel summary', /Po1\(SU\)\s+LACP\s+Gi0\/1\(P\) Gi0\/2\(P\)/) },
      ],
      widgets: [note({ id: 'd2u12-doc', title: 'ROOT CAUSES', prompt: 'Both faults and the evidence.', min: 50 })],
      verifyLabel: 'Verify & close ticket',
    },
  ],
};
